import { useRef, useState, type DragEvent } from "react";
import { FileText, X } from "lucide-react";
import { Button } from "../primitives/button";
import { cn } from "../primitives/utils";

/**
 * Drop a file here, or choose one. Keeps no file itself: the form owns `file`. The hidden input takes the `id` so
 * a FormField label and its error wiring reach it.
 */
export function FilePicker({
  id,
  file,
  onChange,
  accept,
  prompt,
  chooseLabel,
  compact = false,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
}: {
  id: string;
  file: File | null;
  onChange: (file: File | null) => void;
  /** e.g. "application/pdf". */
  accept: string;
  /** "Drop a PDF here, or". */
  prompt: string;
  chooseLabel: string;
  /** Just the button, no drop area (a reply's attachment). */
  compact?: boolean;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [isOver, setIsOver] = useState(false);

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsOver(false);
    const dropped = event.dataTransfer.files[0];
    if (dropped) onChange(dropped);
  };

  const hidden = (
    <input
      ref={input}
      id={id}
      type="file"
      accept={accept}
      className="sr-only"
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedBy}
      onChange={(event) => {
        onChange(event.target.files?.[0] ?? null);
        event.target.value = "";
      }}
    />
  );

  const chosen = file && (
    <span className="inline-flex items-center gap-1.5 text-sm text-foreground">
      <FileText className="size-4 text-muted-foreground" aria-hidden />
      {file.name}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-6"
        aria-label={`Remove ${file.name}`}
        onClick={() => onChange(null)}
      >
        <X />
      </Button>
    </span>
  );

  if (compact) {
    return (
      <span className="inline-flex flex-wrap items-center gap-2">
        {hidden}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => input.current?.click()}
        >
          {chooseLabel}
        </Button>
        {chosen}
      </span>
    );
  }

  return (
    <div
      onDragOver={(event) => {
        event.preventDefault();
        setIsOver(true);
      }}
      onDragLeave={() => setIsOver(false)}
      onDrop={onDrop}
      className={cn(
        "grid justify-items-center gap-2 rounded-lg border-[1.5px] border-dashed p-4 text-center text-sm text-muted-foreground",
        isOver && "border-primary bg-primary/5",
        ariaInvalid && "border-destructive",
      )}
    >
      {hidden}
      {chosen ?? (
        <span>
          {prompt}{" "}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => input.current?.click()}
          >
            {chooseLabel}
          </Button>
        </span>
      )}
    </div>
  );
}
